/**
 * Account Resource
 *
 * Thin 1:1 mapping to account-scoped product APIs (product onboarding).
 * No business logic, no default parameter injection.
 */

import type { HttpLayer } from '../http.js';
import type {
  ProductOnboardingStatus,
  SaveProductOnboardingIntakeRequest,
  ReportProductOnboardingResourcesRequest,
  CompleteProductOnboardingFirstProjectRequest,
  DeferProductOnboardingResponse,
} from '../types/index.js';

export interface WorkstationTermsStatus {
  version: string;
  sha256: string;
  title: string;
  body: string;
  accepted: boolean;
  accepted_at?: string;
}

export interface AcceptWorkstationTermsRequest {
  version: string;
  sha256: string;
}

export class AccountResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/account';

  /** Returns the current workstation-install responsibility terms and this user's acceptance state. */
  async getWorkstationTerms(accountId: string): Promise<WorkstationTermsStatus> {
    return this.http.get(`${this.prefix}/${accountId}/workstation-terms`);
  }

  /** Accepts exactly the immutable terms version and digest returned by getWorkstationTerms. */
  async acceptWorkstationTerms(
    accountId: string,
    params: AcceptWorkstationTermsRequest,
  ): Promise<WorkstationTermsStatus> {
    return this.http.post(
      `${this.prefix}/${accountId}/workstation-terms/accept`,
      params,
    );
  }

  /**
   * Returns the current product-onboarding phase, session_id, and resource IDs
   * for focusing the Shogun onboarding conversation after commercial complete.
   */
  async getProductOnboardingStatus(
    accountId: string,
  ): Promise<ProductOnboardingStatus> {
    return this.http.get(`${this.prefix}/${accountId}/product-onboarding`);
  }

  /**
   * Parks product onboarding in the deferred phase so the user can use the
   * product shell and resume later without completing founder intake.
   */
  async deferProductOnboarding(
    accountId: string,
  ): Promise<DeferProductOnboardingResponse> {
    return this.http.post(
      `${this.prefix}/${accountId}/product-onboarding/defer`,
    );
  }

  /**
   * Persists structured founder intake (who / business-or-interests / intended
   * use) and advances product onboarding past awaiting_intake. Owner-only.
   */
  async saveProductOnboardingIntake(
    accountId: string,
    params: SaveProductOnboardingIntakeRequest,
  ): Promise<ProductOnboardingStatus> {
    return this.http.post(
      `${this.prefix}/${accountId}/product-onboarding/intake`,
      params,
    );
  }

  /**
   * Records created team and workspace IDs (optional project_id) after real
   * WM/device creates and advances product onboarding to first_project.
   * Owner-only.
   */
  async reportProductOnboardingResources(
    accountId: string,
    params: ReportProductOnboardingResourcesRequest,
  ): Promise<ProductOnboardingStatus> {
    return this.http.post(
      `${this.prefix}/${accountId}/product-onboarding/resources`,
      params,
    );
  }

  /**
   * Marks the first project as underway and completes product onboarding.
   * Pass project_id when it was not already recorded via
   * reportProductOnboardingResources. Owner-only.
   */
  async completeProductOnboardingFirstProject(
    accountId: string,
    params: CompleteProductOnboardingFirstProjectRequest = {},
  ): Promise<ProductOnboardingStatus> {
    return this.http.post(
      `${this.prefix}/${accountId}/product-onboarding/first-project`,
      params,
    );
  }
}
